import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Chip,
  IconButton,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Switch,
} from "@mui/material";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardIos";
import LanguageIcon from "@mui/icons-material/Language";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AddIcon from "@mui/icons-material/Add";
import GitHubIcon from "@mui/icons-material/GitHub";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { HEADING_FONT } from "../theme/fonts";
import api from "../api";
import ClientDetailsPanel from "./ClientDetailsPanel";
import InlineAddServer from "./InlineAddServer";
import ServerConnectionForm from "./ServerConnectionForm";

// --- Spec palette (blue · light) ---
const BLUE = "#3B82F6";
const BLUE_DARK = "#1D4ED8";
const BLUE_SOFT = "#EFF6FF";
const INK = "#0F172A";
const MUTED = "#64748B";
const FAINT = "#94A3B8";
const HAIRLINE = "#EEF1F6";
const TILE_BG = "#F8FAFC";

// Small coloured pill for surfacing a fetched value (IP, SSL days, up/down…).
// tone: "info" (blue) · "ok" (green) · "bad" (red) · "muted" (grey).
function StatChip({ label, value, tone = "info", span = 1 }) {
  const tones = {
    info: { fg: BLUE_DARK, bg: BLUE_SOFT },
    ok: { fg: "#15803D", bg: "#DCFCE7" },
    bad: { fg: "#B91C1C", bg: "#FEE2E2" },
    muted: { fg: MUTED, bg: TILE_BG },
  };
  const t = tones[tone] || tones.info;
  return (
    <Box
      sx={{
        // Label above value (not inline) so even a long registrar or IP shows in
        // FULL — it wraps instead of being clipped. `height: 100%` keeps every
        // tile in a grid row the same height when one of them wraps to 2-3 lines.
        gridColumn: `span ${span}`,
        // No `height: 100%` here — grid items already stretch to their row, and
        // a 100% height PLUS padding/border spills the tile past its cell,
        // which is what made the two rows look glued together.
        boxSizing: "border-box",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 0.4,
        px: 1.5,
        py: 1.1,
        borderRadius: "10px",
        background: t.bg,
        border: `1px solid ${HAIRLINE}`,
      }}
    >
      <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: MUTED, lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 700,
          color: t.fg,
          fontFamily: "monospace",
          lineHeight: 1.35,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

const STEPS = [
  { label: "Domain Information", icon: <LanguageIcon sx={{ fontSize: 18 }} /> },
  { label: "Client Details", icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} /> },
  { label: "Server & DNS", icon: <DnsOutlinedIcon sx={{ fontSize: 18 }} /> },
  { label: "Dates & Validity", icon: <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} /> },
  { label: "Deployment", icon: <RocketLaunchOutlinedIcon sx={{ fontSize: 18 }} /> },
];

function computeStatus(expiryDate) {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  return expiry >= today ? "Active" : "Expired";
}

const toDate = (v) => (v ? String(v).slice(0, 10) : "");

// 2048 → "2 GB", 35 → "35 MB". Limits come from the server so the picker can
// never advertise a limit the backend doesn't actually enforce.
const formatMb = (mb) =>
  !mb ? "—" : mb >= 1024 ? `${+(mb / 1024).toFixed(1)} GB` : `${mb} MB`;

// Mirrors toRepoName() in backend/src/integrations/github.js so the toggle
// shows the exact repo name that will be created.
const repoNamePreview = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/[^a-z0-9._-]/g, "-")
    .replace(/^[.-]+|[.-]+$/g, "")
    .slice(0, 100)
    .replace(/[.-]+$/g, "");

// The Add/Edit Domain wizard body (4 steps, per the RABS Workspace spec). Lives
// inside AddDomainModal. Owns all field state + the create/update request, then
// calls onSuccess() so the parent can close the dialog, refresh the list, toast.
// Pass `editId` to load an existing domain and switch to update (PUT) mode.
function AddDomainForm({ onSuccess, onCancel, editId }) {
  const [step, setStep] = useState(0);

  const [domainName, setDomainName] = useState("");
  const [clientCode, setClientCode] = useState("");
  // Linked client id (edit mode) — drives the read-only local client details.
  const [clientId, setClientId] = useState(null);
  // Server + category are normalized FK links now — stored by id (string in
  // form state), resolved to their name on the backend.
  const [serverId, setServerId] = useState("");
  // This domain's own FTP account. aaPanel gives every site its own account,
  // locked to that site's folder, so the details are saved on the domain — not
  // the server, where they would send every other domain into this one's
  // folder. Filled in here so a new domain is ready to deploy straight away.
  const [conn, setConn] = useState(null);
  // What the domain already has (edit mode); null for a new domain.
  const [savedFtp, setSavedFtp] = useState(null);
  const [connEdited, setConnEdited] = useState(false);
  const editConn = (updater) => {
    setConnEdited(true);
    setConn(updater);
  };
  // Full-stack domains (a "… Fullstack" category) deploy to two aaPanel sites:
  // the account above takes the frontend's build, this one the backend. Plus
  // the frontend's build-time .env (API URL…), which never travels in the repo.
  const [backendConn, setBackendConn] = useState(null);
  const [savedBackendFtp, setSavedBackendFtp] = useState(null);
  const [backendConnEdited, setBackendConnEdited] = useState(false);
  const editBackendConn = (updater) => {
    setBackendConnEdited(true);
    setBackendConn(updater);
  };
  const [buildEnv, setBuildEnv] = useState("");
  const [buildEnvEdited, setBuildEnvEdited] = useState(false);
  // A domain can point at several IPs, so these are editable chips (same shape
  // as Name Servers). The linked server's IP is only ever a starting suggestion.
  const [ipAddresses, setIpAddresses] = useState([]);
  const [ipInput, setIpInput] = useState("");
  // Yes/No answer: null = nothing chosen yet, true = Yes (separate hosting → a
  // client-owned server), false = No (a self server). Either way the domain just
  // links to a server; the IP and other details come from that server row.
  const [hasSeparateHosting, setHasSeparateHosting] = useState(null);
  const [categoryId, setCategoryId] = useState("");
  const [registeredDate, setRegisteredDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [registrar, setRegistrar] = useState("");
  const [nameServers, setNameServers] = useState([]);
  const [nameServerInput, setNameServerInput] = useState("");
  const [sslValidity, setSslValidity] = useState("");
  const [cost, setCost] = useState("");
  const [autoRenew, setAutoRenew] = useState(false);
  // Create a GitHub repo for the new domain (add mode only). `github` is the
  // server's integration status — the toggle is hidden when it isn't configured.
  const [createRepo, setCreateRepo] = useState(true);
  const [github, setGithub] = useState({ enabled: false, owner: "", zipLimitMb: 25 });
  // Optional ZIP pushed into the new repo right after the domain is saved. The
  // File object lives only in this component — nothing is stored anywhere.
  const [zipFile, setZipFile] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchNote, setFetchNote] = useState("");
  // Snapshot of the last lookup result, shown as chips under the domain field.
  const [fetched, setFetched] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Button text while the ZIP is going to GitHub ("Uploading… 24 files"), so a
  // multi-minute upload doesn't look like a frozen dialog.
  const [uploadStatus, setUploadStatus] = useState("");

  // Dropdown sources pulled from the backend
  const [servers, setServers] = useState([]);
  const [categories, setCategories] = useState([]);
  // Inline "Add New Server" mini-form under the Server dropdown.
  const [showAddServer, setShowAddServer] = useState(false);

  // Legacy/edge rows may carry only a server/category NAME (no id yet). Stash the
  // name at edit-load time and resolve it to an id once the lists arrive.
  const pendingServerName = useRef("");
  const pendingCategoryName = useRef("");

  useEffect(() => {
    api.get("/servers").then(setServers).catch(() => { });
    api.get("/categories").then(setCategories).catch(() => { });
  }, []);

  // Whether the server can create GitHub repos (add mode only — edit never does).
  useEffect(() => {
    if (editId) return;
    api.get("/domains/github/status").then(setGithub).catch(() => { });
  }, [editId]);

  // Edit mode — load the existing domain once and prefill every field.
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    api
      .get(`/domains/${editId}`)
      .then((d) => {
        if (cancelled || !d) return;
        setDomainName(d.name || "");
        setClientCode(d.clientCode || "");
        setClientId(d.clientId ?? null);
        // Prefer the normalized id; fall back to resolving the stored name once
        // the servers list loads (covers rows saved before ids existed).
        setServerId(d.serverId != null ? String(d.serverId) : "");
        pendingServerName.current = d.serverId != null ? "" : d.serverName || "";
        setSavedFtp(
          d.ftpUsername
            ? {
                host: d.ftpHost || "",
                port: d.ftpPort || 21,
                username: d.ftpUsername,
                root: d.ftpRoot || "",
                passwordSaved: Boolean(d.ftpPasswordSaved),
              }
            : null
        );
        setSavedBackendFtp(
          d.backendFtpUsername
            ? {
                host: d.backendFtpHost || "",
                port: d.backendFtpPort || 21,
                username: d.backendFtpUsername,
                root: d.backendFtpRoot || "",
                passwordSaved: Boolean(d.backendFtpPasswordSaved),
              }
            : null
        );
        setBuildEnv(d.buildEnv || "");
        // Older rows only have the single `ip` — fall back to it so nothing is lost.
        setIpAddresses(
          Array.isArray(d.ipAddresses) && d.ipAddresses.length
            ? d.ipAddresses
            : d.ip
              ? [d.ip]
              : []
        );
        // Yes if the row had separate hosting; No if it has normal server info;
        // otherwise leave unselected.
        const hasServerInfo =
          d.serverId != null ||
          !!d.ip ||
          (Array.isArray(d.nameServers) && d.nameServers.length > 0);
        setHasSeparateHosting(
          d.hasSeparateHosting ? true : hasServerInfo ? false : null
        );
        setCategoryId(d.categoryId != null ? String(d.categoryId) : "");
        pendingCategoryName.current = d.categoryId != null ? "" : d.category || "";
        setRegisteredDate(toDate(d.registered));
        setExpiryDate(toDate(d.expiry));
        setRegistrar(d.registrar || "");
        setNameServers(Array.isArray(d.nameServers) ? d.nameServers : []);
        setSslValidity(d.sslValidity != null ? String(d.sslValidity) : "");
        setCost(d.cost != null ? String(d.cost) : "");
        setAutoRenew(!!d.autoRenew);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [editId]);

  // Resolve a stashed server/category name → its id once the lists load (legacy
  // rows only; rows saved through the form already carry ids).
  useEffect(() => {
    if (!pendingServerName.current || !servers.length) return;
    const m = servers.find((s) => s.name === pendingServerName.current);
    if (m) {
      setServerId(String(m.id));
      if (m.ip) setIpAddresses((prev) => (prev.length ? prev : [m.ip]));
    }
    pendingServerName.current = "";
  }, [servers]);

  useEffect(() => {
    if (!pendingCategoryName.current || !categories.length) return;
    const m = categories.find((c) => c.name === pendingCategoryName.current);
    if (m) setCategoryId(String(m.id));
    pendingCategoryName.current = "";
  }, [categories]);

  const status = computeStatus(expiryDate);

  // Separate hosting = a client-owned server; normal hosting = a self server. The
  // Yes/No choice only filters which servers the dropdown offers. The currently
  // linked server is always kept in the list so an edit never silently clears it.
  const selfServers = servers.filter((s) => s.ownership !== "client");
  const clientServers = servers.filter((s) => s.ownership === "client");
  const serverChoices = (() => {
    const base = hasSeparateHosting === true ? clientServers : selfServers;
    if (serverId && !base.some((s) => String(s.id) === String(serverId))) {
      const cur = servers.find((s) => String(s.id) === String(serverId));
      if (cur) return [cur, ...base];
    }
    return base;
  })();

  // Whatever the chosen server already has, shown on the Deployment step.
  const selectedServer = servers.find((s) => String(s.id) === String(serverId)) || null;

  // What the Deployment step starts from: the domain's own account when it has
  // one (editing), otherwise a starting point — the chosen server's address
  // and aaPanel's usual folder for this domain. Once anything is typed it is
  // left alone.
  useEffect(() => {
    if (connEdited) return;
    if (savedFtp) {
      setConn({
        method: "ftp",
        host: savedFtp.host,
        port: String(savedFtp.port),
        username: savedFtp.username,
        password: "",
        ftpRoot: savedFtp.root,
      });
      return;
    }
    const site = domainName
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    setConn({
      method: "ftp",
      host: selectedServer?.ftpHost || selectedServer?.publicIp || "",
      port: String(selectedServer?.ftpPort || 21),
      username: "",
      password: "",
      ftpRoot: site ? `/www/wwwroot/${site}` : "/www/wwwroot",
    });
  }, [savedFtp, selectedServer?.id, domainName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same for the backend's account — aaPanel usually has it as its own site.
  useEffect(() => {
    if (backendConnEdited) return;
    if (savedBackendFtp) {
      setBackendConn({
        method: "ftp",
        host: savedBackendFtp.host,
        port: String(savedBackendFtp.port),
        username: savedBackendFtp.username,
        password: "",
        ftpRoot: savedBackendFtp.root,
      });
      return;
    }
    const site = domainName
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    setBackendConn({
      method: "ftp",
      host: selectedServer?.ftpHost || selectedServer?.publicIp || "",
      port: String(selectedServer?.ftpPort || 21),
      username: "",
      password: "",
      ftpRoot: site ? `/www/wwwroot/api.${site}` : "/www/wwwroot",
    });
  }, [savedBackendFtp, selectedServer?.id, domainName]); // eslint-disable-line react-hooks/exhaustive-deps

  // A "… Fullstack" category gets the two-account deploy (frontend + backend).
  const selectedCategory = categories.find((c) => String(c.id) === String(categoryId));
  const isFullstack = /full[\s-]?stack/i.test(selectedCategory?.name || "");

  // A server just added via the inline mini-form: add it to the list, select it,
  // and show its IP right away.
  function handleServerCreated(server) {
    setServers((prev) => [...prev, server]);
    setServerId(String(server.id));
    if (server.publicIp) {
      setIpAddresses((prev) =>
        prev.includes(server.publicIp) ? prev : [...prev, server.publicIp]
      );
    }
    setShowAddServer(false);
  }

  function addIp(value) {
    const ip = (value ?? ipInput).trim();
    setIpInput("");
    if (!ip) return;
    setIpAddresses((prev) => (prev.includes(ip) ? prev : [...prev, ip]));
  }

  function removeIp(ip) {
    setIpAddresses((prev) => prev.filter((x) => x !== ip));
  }

  function handleIpKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      addIp();
    }
  }

  function addNameServer() {
    const value = nameServerInput.trim();
    if (!value) return;
    if (nameServers.includes(value)) {
      setNameServerInput("");
      return;
    }
    setNameServers([...nameServers, value]);
    setNameServerInput("");
  }

  function removeNameServer(ns) {
    setNameServers(nameServers.filter((n) => n !== ns));
  }

  function handleNameServerKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      addNameServer();
    }
  }

  // Auto-fill from WHOIS/RDAP + DNS + SSL for the typed domain name. Only
  // overwrites fields the lookup actually resolved.
  async function handleFetch() {
    const name = domainName.trim();
    if (!name || fetching) return;
    setFetching(true);
    setSubmitError("");
    setFetchNote("");
    setFetched(null);
    try {
      const info = await api.get(`/domains/lookup?name=${encodeURIComponent(name)}`);
      if (info.registrar) setRegistrar(info.registrar);
      if (info.registered) setRegisteredDate(info.registered);
      if (info.expiry) setExpiryDate(info.expiry);
      if (Array.isArray(info.nameServers) && info.nameServers.length) {
        setNameServers(info.nameServers);
      }
      if (info.ip) {
        setIpAddresses((prev) => (prev.includes(info.ip) ? prev : [...prev, info.ip]));
      }
      if (info.sslValidity !== null && info.sslValidity !== undefined) {
        setSslValidity(String(info.sslValidity));
      }
      // Snapshot what came back so we can show it as chips under the field.
      setFetched({
        ip: info.ip || "",
        sslValidity:
          info.sslValidity !== null && info.sslValidity !== undefined
            ? Number(info.sslValidity)
            : null,
        registrar: info.registrar || "",
        expiry: info.expiry || "",
        nameServers: Array.isArray(info.nameServers) ? info.nameServers.length : 0,
      });
      if (info.found || info.ip) {
        setFetchNote("Details fetched from WHOIS/DNS");
      } else {
        setSubmitError(
          "Couldn't find registry details for this domain. Check the spelling, or fill them in manually."
        );
      }
    } catch (err) {
      setSubmitError(err.message || "Lookup failed. Please try again.");
    } finally {
      setFetching(false);
    }
  }

  async function handleSubmit() {
    setSubmitError("");
    if (!domainName.trim()) {
      setStep(0);
      setSubmitError("Domain name is required");
      return;
    }

    // Resolve the chosen ids back to names so we send both — the backend treats
    // the id as authoritative and keeps the text column in sync.
    const serverObj = servers.find((s) => String(s.id) === String(serverId));
    const categoryObj = categories.find((c) => String(c.id) === String(categoryId));

    // Hosting details count only when "Yes" is selected — otherwise everything
    // hosting-related is cleared out.
    const separateHosting = hasSeparateHosting === true;

    const payload = {
      name: domainName,
      clientCode: clientCode || null,
      serverId: serverId ? Number(serverId) : null,
      serverName: serverObj?.name || null,
      hasSeparateHosting: separateHosting,
      categoryId: categoryId ? Number(categoryId) : null,
      category: categoryObj?.name || null,
      registered: registeredDate || null,
      expiry: expiryDate,
      registrar,
      nameServers,
      ipAddresses,
      sslValidity: sslValidity ? Number(sslValidity) : null,
      cost: cost === "" ? null : Number(cost),
      autoRenew,
      status: computeStatus(expiryDate) || "Active",
    };
    if (!editId) payload.createRepo = github.enabled && createRepo;

    setSubmitting(true);
    try {
      let saved;
      if (editId) {
        saved = await api.put(`/domains/${editId}`, payload);
      } else {
        saved = await api.post("/domains", payload);
      }

      // The domain's FTP account, once the domain exists to hold it. Saved
      // before any ZIP goes to the repo: the API copies these into the repo's
      // secrets, and the push that follows starts the deploy workflow which
      // reads them. Best-effort — the domain itself is already saved.
      const domainId = editId || saved?.id;
      const fullstackEdited = isFullstack && (backendConnEdited || buildEnvEdited);
      if (domainId && (connEdited || fullstackEdited) && conn?.host && conn?.username) {
        try {
          await api.put(`/domains/${domainId}/ftp`, {
            ftpHost: conn.host,
            ftpPort: conn.port ? Number(conn.port) : 21,
            ftpUsername: conn.username,
            ftpRoot: conn.ftpRoot,
            ...(conn.password ? { ftpPassword: conn.password } : {}),
            ...(isFullstack
              ? {
                  backendFtpHost: backendConn?.host || "",
                  backendFtpPort: backendConn?.port ? Number(backendConn.port) : 21,
                  backendFtpUsername: backendConn?.username || "",
                  backendFtpRoot: backendConn?.ftpRoot || "",
                  ...(backendConn?.password ? { backendFtpPassword: backendConn.password } : {}),
                  buildEnv,
                }
              : {}),
          });
        } catch (ftpErr) {
          saved = { ...saved, ftpError: ftpErr.message || "FTP details not saved" };
        }
      }

      // Optional ZIP → the repo that was just created. Best-effort, like the
      // repo itself: the domain is already saved, so a failure here is reported
      // but never loses the domain. The file goes browser → API → GitHub and is
      // not stored on the way.
      if (!editId && zipFile && saved?.githubRepoStatus === "created") {
        setUploadStatus("Uploading ZIP…");
        // Poll the server's progress while the upload request is open.
        const poll = setInterval(async () => {
          try {
            const p = await api.get(`/domains/${saved.id}/github-upload/status`);
            if (p.phase === "committing") setUploadStatus("Committing…");
            else if (p.files) setUploadStatus(`Uploading… ${p.files} files`);
          } catch {
            /* progress is best-effort — never break the upload over it */
          }
        }, 2000);
        try {
          const form = new FormData();
          form.append("zip", zipFile);
          await api.upload(`/domains/${saved.id}/github-upload`, form);
          saved = { ...saved, zipUploaded: true };
        } catch (zipErr) {
          saved = { ...saved, zipError: zipErr.message || "ZIP upload failed" };
        } finally {
          clearInterval(poll);
          setUploadStatus("");
        }
      }
      // The saved domain lets the parent report the GitHub repo outcome.
      onSuccess?.(saved);
    } catch (err) {
      setSubmitError(err.message || `Failed to ${editId ? "update" : "add"} domain`);
    } finally {
      setSubmitting(false);
    }
  }

  // Continue is blocked on step 1 until a domain name is entered.
  const canContinue = step !== 0 || domainName.trim().length > 0;
  const isLast = step === STEPS.length - 1;

  function handleContinue() {
    if (isLast) {
      handleSubmit();
      return;
    }
    if (!canContinue) {
      setSubmitError("Domain name is required");
      return;
    }
    setSubmitError("");
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  }

  const labelSx = { display: "block", fontSize: 12, fontWeight: 600, color: MUTED, mb: 0.75 };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: "#fff",
      color: INK,
      borderRadius: "10px",
      "& fieldset": { borderColor: HAIRLINE },
      "&:hover fieldset": { borderColor: "#DBE3EF" },
      "&.Mui-focused fieldset": { borderColor: BLUE },
    },
    "& input": { color: INK },
    "& textarea": { color: INK },
    "& .MuiSelect-select": { color: INK },
    "& .MuiSvgIcon-root.MuiSelect-icon": { color: MUTED },
    "& input::placeholder, & textarea::placeholder": { color: FAINT, opacity: 1 },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        background: "#fff",
        border: `1px solid ${HAIRLINE}`,
        "& .MuiMenuItem-root": { color: INK, fontSize: 14 },
        "& .MuiMenuItem-root:hover": { background: TILE_BG },
        "& .MuiMenuItem-root.Mui-selected": { background: BLUE_SOFT },
      },
    },
  };

  // --- Step bodies ---------------------------------------------------------
  const stepDomain = (
    <Stack spacing={2.5}>
      <Box>
        <Typography component="label" sx={labelSx}>Domain Name</Typography>
        <TextField
          placeholder="example.com"
          value={domainName}
          onChange={(e) => setDomainName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleFetch();
            }
          }}
          required
          fullWidth
          sx={fieldSx}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={handleFetch}
                    disabled={fetching || !domainName.trim()}
                    disableElevation
                    startIcon={
                      fetching ? (
                        <CircularProgress size={15} sx={{ color: "#fff" }} />
                      ) : (
                        <TravelExploreIcon sx={{ fontSize: 18 }} />
                      )
                    }
                    sx={{
                      flexShrink: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      borderRadius: "8px",
                      px: 1.75,
                      whiteSpace: "nowrap",
                      color: "#fff",
                      background: BLUE,
                      "&:hover": { background: BLUE_DARK },
                      "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", background: "#BFD3F2" },
                    }}
                  >
                    {fetching ? "Fetching…" : "Fetch"}
                  </Button>
                </InputAdornment>
              ),
            },
          }}
        />
        {/* Fetched-result chips — a quick, colour-coded summary of the lookup. */}
        {fetched && (
          <Box
            sx={{
              mt: 2,
              display: "grid",
              // 3 tiles per row on desktop → the 5 lookup results land on two
              // tidy rows (Registrar spans two columns so the second row fills
              // instead of leaving a hole). `gridAutoRows: 1fr` makes EVERY row
              // the same height, so a tile that wraps to 2 lines doesn't leave
              // its neighbours looking squashed.
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
              gridAutoRows: "1fr",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            {fetched.ip && <StatChip label="IP" value={fetched.ip} tone="info" />}
            {fetched.sslValidity != null && (
              <StatChip
                label="SSL"
                value={`${fetched.sslValidity}d`}
                tone={fetched.sslValidity > 0 ? "ok" : "bad"}
              />
            )}
            {fetched.expiry && <StatChip label="Expiry" value={fetched.expiry} tone="muted" />}
            {fetched.registrar && (
              <StatChip label="Registrar" value={fetched.registrar} tone="muted" span={2} />
            )}
            {fetched.nameServers > 0 && (
              <StatChip label="NS" value={`${fetched.nameServers}`} tone="muted" />
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }}>
        <Box>
          <Typography component="label" sx={labelSx}>Registrar</Typography>
          <TextField
            placeholder="e.g. GoDaddy, Namecheap"
            value={registrar}
            onChange={(e) => setRegistrar(e.target.value)}
            fullWidth
            sx={fieldSx}
          />
        </Box>
        <Box>
          <Typography component="label" sx={labelSx}>Category</Typography>
          <TextField
            select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            fullWidth
            sx={fieldSx}
            slotProps={{
              select: {
                displayEmpty: true,
                renderValue: (selected) => {
                  const c = categories.find((x) => String(x.id) === String(selected));
                  return c ? c.name : <span style={{ color: FAINT }}>Select category</span>;
                },
                MenuProps: selectMenuProps,
              },
            }}
          >
            <MenuItem value=""><span style={{ color: FAINT }}>Select category</span></MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </Stack>
  );

  const stepClient = (
    <ClientDetailsPanel
      value={clientCode}
      onChange={(code) => setClientCode(code)}
      colors={{ text: INK, textMuted: MUTED, textFaint: FAINT, cardBorder: HAIRLINE, hover: TILE_BG }}
      fieldSx={fieldSx}
      labelSx={labelSx}
      // Add = CRM Fetch; Edit = read-only details from our own DB (no Fetch).
      source={editId ? "local" : "crm"}
      clientId={clientId}
    />
  );

  const stepServer = (
    <Stack spacing={2.5}>
      {/* Yes/No gate — decides whether the hosting detail fields are shown.
          Nothing is pre-selected; the user must pick Yes or No. */}
      <Box>
        <Typography component="label" sx={labelSx}>Does this domain have separate hosting?</Typography>
        <Stack direction="row" spacing={1.5}>
            {[
              { val: true, text: "Yes" },
              { val: false, text: "No" },
            ].map((opt) => {
              const selected = hasSeparateHosting === opt.val;
              return (
                <Button
                  key={opt.text}
                  onClick={() => setHasSeparateHosting(opt.val)}
                  disableElevation
                  sx={{
                    minWidth: 96,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 14,
                    py: 1,
                    borderRadius: "10px",
                    color: selected ? "#fff" : MUTED,
                    background: selected ? BLUE : "#fff",
                    border: `1px solid ${selected ? BLUE : HAIRLINE}`,
                    "&:hover": {
                      background: selected ? BLUE_DARK : TILE_BG,
                      borderColor: selected ? BLUE_DARK : "#DBE3EF",
                    },
                  }}
                >
                  {opt.text}
                </Button>
              );
            })}
          </Stack>
        </Box>

      {hasSeparateHosting !== null && (
        <Stack spacing={2.5} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${HAIRLINE}`, background: TILE_BG }}>
          <Box>
            <Typography component="label" sx={labelSx}>Server Name</Typography>
            <TextField
              select
              value={serverId}
              onChange={(e) => {
                const val = e.target.value;
                // "+ Add New Server" opens the inline mini-form instead of
                // selecting a server.
                if (val === "__add_new__") {
                  setShowAddServer(true);
                  return;
                }
                setServerId(val);
                // The server's IP is a suggestion, not the truth — seed it only
                // when the user hasn't listed any IP of their own yet, so
                // switching servers never wipes a hand-entered list.
                const match = servers.find((s) => String(s.id) === String(val));
                if (match?.publicIp) {
                  setIpAddresses((prev) => (prev.length ? prev : [match.publicIp]));
                }
              }}
              fullWidth
              sx={fieldSx}
              slotProps={{
                select: {
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const s = servers.find((x) => String(x.id) === String(selected));
                    return s ? s.name : <span style={{ color: FAINT }}>Select server</span>;
                  },
                  MenuProps: selectMenuProps,
                },
              }}
            >
              <MenuItem value=""><span style={{ color: FAINT }}>Select server</span></MenuItem>
              {serverChoices.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
              ))}
              <MenuItem value="__add_new__" sx={{ fontWeight: 600, color: BLUE }}>
                + Add New Server
              </MenuItem>
            </TextField>
            {hasSeparateHosting && (
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.75 }}>
                Only client-owned servers are shown — pick one or add a new server below.
              </Typography>
            )}
            {showAddServer && (
              <InlineAddServer
                ownership={hasSeparateHosting === true ? "client" : "self"}
                onCreated={handleServerCreated}
                onCancel={() => setShowAddServer(false)}
                fieldSx={fieldSx}
                labelSx={labelSx}
              />
            )}
          </Box>

          <Box sx={{ display: showAddServer ? "none" : "block" }}>
            <Typography component="label" sx={labelSx}>IP Addresses</Typography>
            {/* Editable: a domain can point at several IPs. The selected
                server's IP is seeded as a suggestion; add or remove freely. */}
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="e.g. 103.239.124.50"
                value={ipInput}
                onChange={(e) => setIpInput(e.target.value)}
                onKeyDown={handleIpKeyDown}
                fullWidth
                sx={fieldSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <DnsOutlinedIcon sx={{ fontSize: 18, color: FAINT, mr: 1 }} />
                    ),
                  },
                }}
              />
              <IconButton
                onClick={() => addIp()}
                sx={{
                  flexShrink: 0,
                  width: 56,
                  height: 56,
                  borderRadius: "10px",
                  color: "#fff",
                  background: BLUE,
                  "&:hover": { background: BLUE_DARK },
                }}
              >
                <AddIcon />
              </IconButton>
            </Stack>
            {ipAddresses.length > 0 ? (
              <Box
                sx={{
                  mt: 1.5,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1,
                  minWidth: 0,
                }}
              >
                {ipAddresses.map((ip) => (
                  <Chip
                    key={ip}
                    label={ip}
                    title={ip}
                    onDelete={() => removeIp(ip)}
                    sx={{
                      maxWidth: "100%",
                      justifyContent: "space-between",
                      background: BLUE_SOFT,
                      color: BLUE_DARK,
                      border: `1px solid ${HAIRLINE}`,
                      fontFamily: "monospace",
                      fontWeight: 700,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiChip-deleteIcon": { color: BLUE, "&:hover": { color: BLUE_DARK } },
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.75 }}>
                Pick a server to seed its IP, or type one and press Enter.
              </Typography>
            )}
          </Box>

          <Box>
            <Typography component="label" sx={labelSx}>Name Servers</Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="ns1.example.com"
                value={nameServerInput}
                onChange={(e) => setNameServerInput(e.target.value)}
                onKeyDown={handleNameServerKeyDown}
                fullWidth
                sx={fieldSx}
              />
              <IconButton
                onClick={addNameServer}
                sx={{
                  flexShrink: 0,
                  width: 56,
                  height: 56,
                  borderRadius: "10px",
                  color: "#fff",
                  background: BLUE,
                  "&:hover": { background: BLUE_DARK },
                }}
              >
                <AddIcon />
              </IconButton>
            </Stack>
            {nameServers.length > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  display: "grid",
                  // Long name-servers (e.g. reha670640.mercury.orderbox-dns.com)
                  // get their own full-width row; short ones pack 2-per-row.
                  gridTemplateColumns: nameServers.some((ns) => ns.length > 22)
                    ? "1fr"
                    : "1fr 1fr",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                {nameServers.map((ns) => (
                  <Chip
                    key={ns}
                    label={ns}
                    title={ns}
                    onDelete={() => removeNameServer(ns)}
                    sx={{
                      maxWidth: "100%",
                      background: BLUE_SOFT,
                      color: BLUE_DARK,
                      border: `1px solid ${HAIRLINE}`,
                      fontWeight: 500,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiChip-deleteIcon": { color: BLUE, "&:hover": { color: BLUE_DARK } },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );

  const stepDates = (
    <Stack spacing={2.5}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }}>
        <Box>
          <Typography component="label" sx={labelSx}>Registered Date</Typography>
          <TextField type="date" value={registeredDate} onChange={(e) => setRegisteredDate(e.target.value)} fullWidth sx={fieldSx} />
        </Box>
        <Box>
          <Typography component="label" sx={labelSx}>Expiry Date</Typography>
          <TextField type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} required fullWidth sx={fieldSx} />
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }}>
        <Box>
          <Typography component="label" sx={labelSx}>SSL Validity (Days)</Typography>
          <TextField type="number" placeholder="e.g. 90" value={sslValidity} onChange={(e) => setSslValidity(e.target.value)} fullWidth inputProps={{ min: 0 }} sx={fieldSx} />
        </Box>
        <Box>
          <Typography component="label" sx={labelSx}>Computed Status</Typography>
          <Box
            sx={{
              height: 56,
              px: 2,
              borderRadius: "10px",
              border: `1px solid ${HAIRLINE}`,
              background: TILE_BG,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: !status ? FAINT : status === "Active" ? "#16A34A" : "#DC2626",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {status === "Active" && <CheckCircleOutlinedIcon sx={{ fontSize: 20 }} />}
            {status === "Expired" && <ErrorOutlinedIcon sx={{ fontSize: 20 }} />}
            {status || "Set automatically from expiry date"}
          </Box>
        </Box>
      </Box>

      {/* Renewal & Billing (spec §4.8) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5, alignItems: "start" }}>
        <Box>
          <Typography component="label" sx={labelSx}>Renewal Cost (₹)</Typography>
          <TextField type="number" placeholder="e.g. 999" value={cost} onChange={(e) => setCost(e.target.value)} fullWidth inputProps={{ min: 0, step: "0.01" }} sx={fieldSx} />
        </Box>
        <Box>
          <Typography component="label" sx={labelSx}>Renewal</Typography>
          <Box sx={{ display: "flex", alignItems: "center", height: 56 }}>
            <Switch
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
              sx={{
                ml: -1,
                "& .MuiSwitch-switchBase.Mui-checked": { color: BLUE },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: BLUE },
              }}
            />
            <Typography
              component="span"
              sx={{
                ml: -1,
                fontSize: 13.5,
                fontWeight: 600,
                lineHeight: 1,
                color: autoRenew ? BLUE_DARK : MUTED,
              }}
            >
              {autoRenew ? "Automatic" : "Manual"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* GitHub repo — add mode only, and only when the server has it configured */}
      {!editId && github.enabled && (
        <Box>
          <Typography component="label" sx={labelSx}>GitHub Repository</Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minHeight: 56,
              px: 2,
              borderRadius: "10px",
              border: `1px solid ${HAIRLINE}`,
              background: TILE_BG,
            }}
          >
            <GitHubIcon sx={{ fontSize: 20, color: createRepo ? BLUE_DARK : MUTED }} />
            <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: createRepo ? BLUE_DARK : MUTED, wordBreak: "break-all" }}>
              {createRepo
                ? `Create repo ${github.owner}/${repoNamePreview(domainName) || "…"}`
                : "Don't create a repo"}
            </Typography>
            <Switch
              checked={createRepo}
              onChange={(e) => {
                setCreateRepo(e.target.checked);
                if (!e.target.checked) setZipFile(null); // no repo → no upload
              }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: BLUE },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: BLUE },
              }}
            />
          </Box>

          {/* Optional ZIP → the repo's files, in one commit. */}
          {createRepo && (
            <Box sx={{ mt: 1.5 }}>
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minHeight: 52,
                  px: 2,
                  borderRadius: "10px",
                  border: `1px dashed ${zipFile ? BLUE : HAIRLINE}`,
                  background: TILE_BG,
                  cursor: "pointer",
                }}
              >
                <FolderZipOutlinedIcon sx={{ fontSize: 20, color: zipFile ? BLUE_DARK : MUTED }} />
                <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: zipFile ? BLUE_DARK : MUTED, wordBreak: "break-all" }}>
                  {zipFile
                    ? `${zipFile.name} (${(zipFile.size / 1048576).toFixed(1)} MB)`
                    : `Upload a ZIP into the repo (optional, max ${formatMb(github.zipLimitMb)})`}
                </Typography>
                {zipFile && (
                  <Typography
                    component="span"
                    onClick={(e) => {
                      e.preventDefault();
                      setZipFile(null);
                    }}
                    sx={{ fontSize: 12, fontWeight: 700, color: MUTED, "&:hover": { color: BLUE_DARK } }}
                  >
                    Remove
                  </Typography>
                )}
                <input
                  type="file"
                  accept=".zip,application/zip"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    // Catch an oversized ZIP here rather than after minutes of
                    // uploading. `hardLimitMb` is the real cap the server
                    // enforces (the label advertises a smaller number).
                    const cap = github.hardLimitMb || github.zipLimitMb;
                    if (file && cap && file.size > cap * 1048576) {
                      setSubmitError(
                        `"${file.name}" is ${(file.size / 1048576).toFixed(0)} MB. The limit is ${cap} MB — split it, or leave large media out of the archive.`
                      );
                      setZipFile(null);
                      e.target.value = "";
                      return;
                    }
                    setSubmitError("");
                    setZipFile(file);
                  }}
                />
              </Box>
              <Typography sx={{ mt: 0.75, fontSize: 11.5, color: MUTED }}>
                The ZIP is unpacked straight into the repo — it is never saved on the server.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );

  // How this domain's code reaches its server: the site's own FTP account.
  const stepDeploy = (
    <Stack spacing={2}>
      {isFullstack && (
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          Full-stack project: one 🚀 Deploy runs <b>npm install</b> for the backend and uploads it with
          node_modules, then <b>npm install + npm run build</b> for the frontend and uploads the build.
          Upload a ZIP with a <b>backend/</b> and a <b>frontend/</b> folder.
        </Typography>
      )}
      <ServerConnectionForm
        value={conn}
        onChange={editConn}
        serverName={isFullstack ? `Frontend FTP — ${domainName.trim() || "this domain"}` : domainName.trim() || "This domain"}
        configured={Boolean(savedFtp?.passwordSaved)}
        busy={submitting}
        lockMethod="ftp"
        alwaysOpen
        note="Saved on this domain only (encrypted), and copied into its GitHub repo's secrets so a push deploys automatically."
      />
      {isFullstack && (
        <>
          <ServerConnectionForm
            value={backendConn}
            onChange={editBackendConn}
            serverName={`Backend FTP — ${domainName.trim() || "this domain"}`}
            configured={Boolean(savedBackendFtp?.passwordSaved)}
            busy={submitting}
            lockMethod="ftp"
            alwaysOpen
            note="The backend's own aaPanel site. Its .env, uploads and logs on the server are never overwritten."
          />
          <TextField
            label="Frontend build settings (.env) — optional"
            placeholder={"REACT_APP_API_URL=https://api.example.com\nVITE_API_URL=https://api.example.com"}
            value={buildEnv}
            onChange={(e) => {
              setBuildEnvEdited(true);
              setBuildEnv(e.target.value);
            }}
            multiline
            minRows={3}
            fullWidth
            disabled={submitting}
            helperText="Written as frontend/.env just before npm run build. These values end up in the public JavaScript — never put passwords here."
            sx={fieldSx}
          />
        </>
      )}
      <Typography sx={{ fontSize: 11.5, color: MUTED }}>
        Optional — the domain is added either way. Without these the 🚀 Deploy button will ask for
        them the first time it is used.
      </Typography>
    </Stack>
  );

  const stepBodies = [stepDomain, stepClient, stepServer, stepDates, stepDeploy];

  return (
    <Box sx={{ display: "flex", minHeight: { sm: 460 }, maxHeight: { xs: "90vh", sm: "88vh" } }}>
      {/* Blue left rail with the 4 steps (gradient allowed on the rail per spec) */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          flexDirection: "column",
          width: 210,
          flexShrink: 0,
          p: 3,
          color: "#fff",
          background: `linear-gradient(170deg, ${BLUE_DARK} 0%, ${BLUE} 100%)`,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", opacity: 0.8 }}>
            {editId ? "Edit" : "New"}
          </Typography>
          <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 20, fontWeight: 700, letterSpacing: "-0.4px" }}>
            {editId ? "Edit Domain" : "Add Domain"}
          </Typography>
        </Box>
        <Stack spacing={0.5} sx={{ flex: 1 }}>
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <Stack
                key={s.label}
                direction="row"
                alignItems="center"
                spacing={1.5}
                onClick={() => setStep(i)}
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: "10px",
                  cursor: "pointer",
                  background: active ? "rgba(255,255,255,0.18)" : "transparent",
                  transition: "background 0.15s ease",
                  "&:hover": { background: "rgba(255,255,255,0.12)" },
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: active ? BLUE_DARK : "#fff",
                    background: active ? "#fff" : "rgba(255,255,255,0.18)",
                    border: done ? "none" : "1px solid rgba(255,255,255,0.35)",
                  }}
                >
                  {done ? <CheckIcon sx={{ fontSize: 15 }} /> : i + 1}
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: active ? 700 : 500, opacity: active || done ? 1 : 0.85 }}>
                  {s.label}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ p: 3, flex: 1, minHeight: 0, overflowY: "auto" }}>
          {/* Mobile step indicator */}
          <Typography sx={{ display: { sm: "none" }, fontSize: 11, fontWeight: 700, color: BLUE, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1 }}>
            Step {step + 1} of {STEPS.length}
          </Typography>

          {/* Step title */}
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                background: BLUE_SOFT,
                color: BLUE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {STEPS[step].icon}
            </Box>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 17, fontWeight: 700, color: INK }}>
              {STEPS[step].label}
            </Typography>
          </Stack>

          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError("")} sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          {fetchNote && (
            <Alert severity="success" onClose={() => setFetchNote("")} sx={{ mb: 2 }}>
              {fetchNote}
            </Alert>
          )}

          {stepBodies[step]}
        </Box>

        {/* Footer nav — buttons pushed to the far right edge of the content. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ pl: 3, pr: 5, py: 2, borderTop: `1px solid ${HAIRLINE}`, flexShrink: 0 }}
        >
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={step === 0 ? onCancel : () => setStep((s) => Math.max(0, s - 1))}
              startIcon={step === 0 ? null : <ArrowBackIcon sx={{ fontSize: 13 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                py: 1,
                borderRadius: "10px",
                color: MUTED,
                background: "#fff",
                border: `1px solid ${HAIRLINE}`,
                "&:hover": { background: TILE_BG, borderColor: "#DBE3EF" },
              }}
            >
              {step === 0 ? "Cancel" : "Back"}
            </Button>
            <Button
              onClick={handleContinue}
              disabled={submitting}
              endIcon={
                submitting ? (
                  <CircularProgress size={15} sx={{ color: "#fff" }} />
                ) : isLast ? null : (
                  <ArrowForwardIcon sx={{ fontSize: 13 }} />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: "10px",
                color: "#fff",
                background: BLUE,
                boxShadow: "none",
                "&:hover": { background: BLUE_DARK, boxShadow: "none" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", background: "#BFD3F2" },
              }}
            >
              {isLast
                ? submitting
                  ? editId
                    ? "Saving…"
                    : uploadStatus || "Adding…"
                  : editId
                    ? "Update Domain"
                    : "Add Domain"
                : "Continue"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

export default AddDomainForm;

